import { BaseScreen } from '@goodboydigital/astro';
import { Point, Sprite, Texture } from 'pixi.js';

import { App, Shared } from '../App';
import { AnalyticsEvent, AnalyticsState } from '../plugins/AnalyticsPlugin';
import { NineSliceButton } from '../ui/buttons/NineSliceButton';
import LocalisedText from '../ui/LocalisedText';
import { StatsWidget } from '../ui/StatsWidget';
import { SelectPanelButton } from './../ui/buttons/SelectPanelButton';
import { Panel } from './../ui/Panel';

export class GarageScreen extends BaseScreen
{
    public app: App;
    public manifests: string[] = [];
    public bg!: Sprite;
    public title!: LocalisedText;
    public backButton!: NineSliceButton;
    public characterPanel!: Panel;
    public studioPanel!: Panel;
    public playButton!: NineSliceButton;

    public characterPanelButton!: SelectPanelButton;
    public rideStudioButton!: SelectPanelButton;

    public characterIcon!: Sprite;
    public vehicleIcon!: Sprite;

    public iconHash: Record<string, Texture>;

    private _stats!: StatsWidget;

    constructor(app: App)
    {
        super(app);
        this.app = app;
        this.iconHash = {};
    }

    async init(): Promise<void>
    {
        this.bg = Sprite.from(Texture.WHITE);
        this.bg.alpha = 0;
        this.bg.width = this.bg.height = 9999;
        this.bg.anchor.set(0.5);
        this.view.addChild(this.bg);

        const backIcon = Sprite.from(Shared.assets.getTexture('icon-home'));

        this.backButton = new NineSliceButton({
            icon: backIcon,
            bgAsset: 'button-ninesliced-yellow',
            accessibleTitle: 'Back',
            w: backIcon.width * 1.5,
            h: backIcon.height * 1.5,
        });
        this.view.addChild(this.backButton.view);
        this.backButton.onPress.connect(() =>
        {
            this.app.controller.toHome();
        });

        this.title = new LocalisedText('garageScreen_title', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            align: 'center',
            lineJoin: 'round',
            fontSize: 54,
            maxWidth: 450,
        });
        this.title.anchor.set(0.5);
        this.view.addChild(this.title);

        this.characterPanel = new Panel({
            w: 350,
            h: 400,
        });
        this.view.addChild(this.characterPanel.view);

        this._createIconHash();

        const keys = Object.keys(this.iconHash);

        this.characterIcon = Sprite.from(this.iconHash[keys[0]]);
        this.characterIcon.anchor.set(0.5);

        this.characterPanelButton = new SelectPanelButton({
            buttonText: 'garageScreen_charactersSelect',
            titleText: 'garageScreen_characters',
            characterSprite: this.characterIcon,
        });
        this.characterPanelButton.panelTarget = this.characterPanel;

        this.characterPanelButton.view.x += 20;
        this.characterPanelButton.onPress.connect(() =>
        {
            this.app.analytics.trackEvent(AnalyticsEvent.CUSTOMISEPAGE_ITEMSELECTION_CLICK, { game_item: "characters" });
            this.app.controller.toCharacterSelect();
        });
        this.characterPanel.view.addChild(this.characterPanelButton.view);
        this.characterIcon.scale.set(1.2);

        this.studioPanel = new Panel({
            w: 350,
            h: 400,
        });
        this.view.addChild(this.studioPanel.view);

        this.vehicleIcon = new Sprite();
        this.vehicleIcon.anchor.set(0.5);

        this.rideStudioButton = new SelectPanelButton({
            buttonText: 'garageScreen_rideStudioSelect',
            titleText: 'garageScreen_rideStudio',
            characterSprite: this.vehicleIcon,
            itemHolderAsset: '',
        });
        this.rideStudioButton.panelTarget = this.studioPanel;

        this.rideStudioButton.view.x -= 20;

        this.rideStudioButton.onPress.connect(() =>
        {
            this.app.analytics.trackEvent(AnalyticsEvent.CUSTOMISEPAGE_ITEMSELECTION_CLICK, { game_item: "garage" });
            this.app.controller.toRideStudio();
        });
        this.studioPanel.view.addChild(this.rideStudioButton.view);

        this._stats = new StatsWidget();
        this.view.addChild(this._stats.view);

        const playIcon = new Sprite();
        const playText = new LocalisedText('garageScreen_play', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            align: 'center',
            lineJoin: 'round',
            fontSize: 44,
            maxWidth: 250,
        });

        playText.anchor.set(0.5);
        playIcon.addChild(playText);

        this.playButton = new NineSliceButton({
            icon: playIcon,
            accessibleTitle: 'GO',
            bgAsset: 'button-ninesliced-magenta',
            w: 275,
            h: 100,
        });
        this.view.addChild(this.playButton.view);
        this.playButton.onPress.connect(() => {
            this.app.analytics.trackEvent(AnalyticsEvent.CUSTOMISEPAGE_RACE_CLICK);
            this.app.controller.toPreRace()
        });
    }

    show(): void
    {
        this.resize(this.width, this.height);
        
        this.app.analytics.trackState(AnalyticsState.CUSTOMISE);

        Shared.game.soundSystem.playMusic('menu-music');

        Shared.game.hud.showCoinsAndDiamonds();

        if (Shared.game)
        {
            const garage = Shared.game.garage;

            this.characterIcon.texture = this.iconHash[garage.selectedCharacter];
            this.vehicleIcon.removeChildren();

            const char = garage.getPlainSprite({
                chassis: garage.selectedChassis,
                wheelLeft: garage.selectedWheelLeft,
                wheelRight: garage.selectedWheelRight,
                color: garage.selectedColorChassis,
                wheelLeftColor: garage.selectedColorWheelLeft,
                wheelRightColor: garage.selectedColorWheelRight,
            });

            char.scale.set(140 / char.width);
            char.y -= 20;

            this.vehicleIcon.addChild(char);

            this._stats.open();
        }
        this.view.visible = true;        
    }

    hidden(): void
    {
        this.view.visible = false;

        this._stats.close();
    }

    resize(w: number, h: number): void
    {
        super.resize(w, h);
        this.view.x = (this.width - this.app.width) * 0.5;
        this.view.y = (this.height - this.app.height) * 0.5;
        this.bg.position.copyFrom(this.app.stageCenter as Point);

        this.backButton.view.position.set(
            this.app.stageLeft + 90,
            this.app.stageTop + 90,
        );

        this.title.x = this.app.stageCenter.x;
        this.title.y = this.app.stageTop + (this.title.height / 2) + 20;

        this.characterPanel.view.position.set(
            this.app.stageLeft + (this.characterPanel.bg.width / 2) - 45,
            this.app.stageCenter.y,
        );
        this.characterPanelButton.originalX = this.characterPanel.view.x;
        this.characterPanelButton.offsetX = this.characterPanel.view.x + 10;

        this.studioPanel.view.position.set(
            this.app.stageRight - (this.studioPanel.bg.width / 2) + 45,
            this.app.stageCenter.y,
        );
        this.rideStudioButton.originalX = this.studioPanel.view.x;
        this.rideStudioButton.offsetX = this.studioPanel.view.x - 10;

        this.playButton.view.position.set(
            this.app.stageRight - 170,
            this.app.stageBottom - 90);

        this._stats.view.x = this.app.stageCenter.x + 55;
        this._stats.view.y = this.app.stageBottom - 300;
    }

    private _createIconHash(): void
    {
        const data = this.app.assets.data;

        for (const f in data)
        {
            if (f.startsWith('character-'))
            {
                const charMeta = data[f] as any;

                this.iconHash[charMeta.id as string] = Shared.assets.getTexture(charMeta.iconId);
            }
        }
    }
}
